import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { LogOut, Shield, Video, Users, Trophy, Menu, X, User, UserPlus, MessageSquare, Search, Layers, Tv, Film, Hash, BarChart3, Newspaper, ShoppingBag, Coins, Package, ChevronDown, Gamepad2, AlertTriangle, Flag } from 'lucide-react';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useSwipeable } from 'react-swipeable';
import { friendAPI, searchAPI, dragonBallAPI, shopAPI } from '@/utils/api';
import { motion, AnimatePresence } from 'framer-motion';
import { DragonBallCollection } from '@/components/DragonBallCollector';
import { AdFreeIndicator } from '@/components/AdFreeIndicator';
import { ShenronWishModal } from '@/components/ShenronWishModal';
import { ReportModal } from '@/components/ReportModal';
import { subscribeToEvent, emitEvent, RADAR_OPEN, RADAR_SUMMON, DRAGON_BALL_COLLECTED, DRAGON_BALL_WISH_MADE, FRIEND_REQUEST_SENT, FRIEND_REQUEST_ACCEPTED, FRIEND_REQUEST_REJECTED, FRIEND_REMOVED } from '@/utils/events';
import { Radar } from 'lucide-react';
import { HalloweenShopButton } from '@/components/halloween/HalloweenShop';
import { FLASH_GAMES } from '@/pages/GamesPage';

// Public routes actually registered in App.js; no invented destinations.
const SEARCH_PAGES = [
  ['Video Library', '/library'], ['Games', '/games'], ['Global Chat', '/chat'],
  ['Chat Rooms', '/channels'], ['Leaderboard', '/leaderboard'],
  ['News', '/news'], ['Zeni Shop', '/shop'], ['Inventory', '/inventory'],
  ['Friends', '/friends'], ['Friend Requests', '/friends'],
  ['Direct Messages', '/groups'], ['Group Chats', '/groups'],
  ['My Profile', '/profile'], ['Transactions', '/transactions'],
];
const searchRank = (label, query) => label.toLowerCase().startsWith(query) ? 0 : 1;


const SHENRON_RESULT_GROUPS = [
  { title: 'Users', types: ['user'] },
  { title: 'Games', types: ['game'] },
  { title: 'Pages', types: ['page'] },
  { title: 'Series', types: ['series'] },
  { title: 'Sagas & Seasons', types: ['season'] },
  { title: 'Episodes', types: ['episode'] },
];

const ShenronSearchResults = ({ results, query, loading, error, onSelect, onDM, mobile = false, compact = false }) => {
  const [expanded, setExpanded] = useState(false);
  useEffect(() => setExpanded(false), [query]);

  return (
    <div
      className={`relative overflow-hidden rounded-b-[22px] rounded-t-[8px] border-[3px] border-t-0 border-[#f4b73c] ${compact ? "px-2 pb-2 pt-1" : "p-3 sm:p-4"} ${mobile ? 'w-full' : 'w-full'}`}
      style={{
        background: 'radial-gradient(circle at 78% 30%, rgba(41,91,48,.35), transparent 50%), linear-gradient(145deg, rgba(1,19,12,.99), rgba(1,37,22,.99) 60%, rgba(2,15,11,.99))',
        boxShadow: '0 0 0 1px #7a3d08, 0 0 19px rgba(255,178,37,.65), inset 0 0 25px rgba(6,48,25,.85)',
      }}
      data-testid={mobile ? 'mobile-search-results' : 'nav-search-results'}
    >
      <div aria-hidden="true" className="pointer-events-none absolute right-5 top-12 select-none text-[140px] leading-none font-bold text-amber-400/[.045]">悟</div>
      <div className={`relative max-h-[min(65vh,560px)] overflow-y-auto pr-1 ${compact ? "pt-[20%]" : "pt-[22%]"}`}>
        {loading ? (
          <p className="py-6 text-center italic text-[#ffe1a3]" style={{ fontFamily: 'Georgia, serif' }}>Searching the Dragon TV universe...</p>
        ) : error ? (
          <p className="py-6 text-center text-red-300">Search is unavailable. Please try again.</p>
        ) : results.length ? (
          <>
            {SHENRON_RESULT_GROUPS.map(group => {
              const matches = results.filter(result => group.types.includes(result.type));
              if (!matches.length) return null;
              return (
                <section key={group.title} className={compact ? "mb-2 last:mb-0" : "mb-3 last:mb-0"}>
                  <div className={compact ? "mb-1 flex items-center gap-1.5" : "mb-2 flex items-center gap-2"}>
                    <span aria-hidden="true" className={`${compact ? "h-5 w-5 text-[10px]" : "h-7 w-7"} flex shrink-0 items-center justify-center rounded-full border border-[#ffef97] text-[#a52e06] shadow-[0_0_12px_rgba(255,167,29,.7)]`} style={{ background: 'radial-gradient(circle at 30% 25%, #fff4aa, #ffa619 55%, #b94108)' }}>★</span>
                    <h3 className={`${compact ? "text-sm" : "text-lg sm:text-xl"} shrink-0 font-bold italic text-[#ffe3a3]`} style={{ fontFamily: 'Georgia, Times New Roman, serif', textShadow: '0 1px 2px #3b1200, 0 0 12px rgba(255,161,30,.7)' }}>{group.title}</h3>
                    <div className="h-px flex-1 bg-gradient-to-r from-[#ffcb53] to-transparent" />
                  </div>
                  {(expanded ? matches : matches.slice(0, 2)).map((result, index) => {
                    const Icon = result.type === 'user' ? User : result.type === 'game' ? Gamepad2 : result.type === 'page' ? Layers : result.type === 'episode' ? Film : result.type === 'season' ? Tv : Video;
                    const image = result.thumbnail_url || result.banner_image || result.poster_url || result.avatar_url || result.avatar;
                    return (
                      <div key={`${result.type}-${result.id}`} className={`${compact ? "mb-1.5 gap-1 p-1" : "mb-2 gap-2 p-2"} group flex items-center rounded-xl border border-[#e6ae37] bg-gradient-to-r from-[#03140e]/90 via-[#06301c]/85 to-[#04170e]/90 shadow-[inset_0_0_12px_rgba(6,42,24,.8)] transition-all duration-200 hover:border-[#ffe58a] hover:shadow-[0_0_15px_rgba(255,196,54,.4)]`}>
                        <button type="button" onClick={() => onSelect(result)} className={`${compact ? "gap-1.5" : "gap-3"} flex min-w-0 flex-1 items-center text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-amber-300`} data-testid={`${mobile ? 'mobile' : 'nav'}-search-result-${group.title}-${index}`}>
                          <div className={`${compact ? "h-8 w-9" : "h-12 w-14 sm:h-14 sm:w-20"} flex shrink-0 items-center justify-center overflow-hidden rounded-lg border border-amber-400/65 bg-gradient-to-br from-[#184f39] to-[#42250d]`}>
                            {image ? <img src={image} alt="" loading="lazy" className="h-full w-full object-cover" onError={e => { e.currentTarget.style.display = 'none'; }} /> : <Icon className={compact ? "h-4 w-4 text-amber-300/80" : "h-6 w-6 text-amber-300/80"} />}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className={`${compact ? "text-xs" : "text-sm sm:text-base"} truncate font-semibold italic text-[#fff0d1]`} style={{ fontFamily: 'Georgia, Times New Roman, serif', textShadow: '0 0 7px rgba(255,187,75,.4)' }}>{result.name}</div>
                            <div className={`${compact ? "text-[10px]" : "text-xs"} truncate italic text-[#f9ddb0]/80`}>{result.series_name || result.role || (result.type === 'user' ? 'Member profile' : result.type === 'game' ? 'Play game' : result.type === 'page' ? 'Open page' : result.type === 'episode' ? 'Episode' : result.type === 'season' ? 'Saga / Season' : 'TV Series')}{result.episode_number != null ? ` • Episode ${result.episode_number}` : ''}</div>
                          </div>
                          <span aria-hidden="true" className={`${compact ? "text-base" : "text-2xl"} text-[#ffd25d] transition-transform group-hover:translate-x-1`}>›</span>
                        </button>
                        {result.type === 'user' && <button type="button" onClick={() => onDM(result)} className={`${compact ? "px-1 py-0.5 text-[10px]" : "px-2 py-1 text-xs"} shrink-0 rounded-lg border border-amber-400/65 italic text-[#ffe2a2] hover:bg-amber-400/20`} aria-label={`Message ${result.name}`}>DM</button>}
                      </div>
                    );
                  })}
                </section>
              );
            })}
            {results.length > SHENRON_RESULT_GROUPS.length * 2 && (
              <button type="button" onClick={() => setExpanded(!expanded)} className="mt-2 flex w-full items-center justify-between rounded-xl border-2 border-[#e8a83c] bg-[#061e13]/85 px-3 py-2 text-left italic text-[#ffe2a2] shadow-[0_0_9px_rgba(255,180,43,.3)] hover:bg-[#20452a]" style={{ fontFamily: 'Georgia, Times New Roman, serif' }}>
                <span><Search className="mr-2 inline h-4 w-4 text-amber-400" />{expanded ? 'Show Fewer Results' : `View All Results for “${query}”`}</span>
                <span aria-hidden="true" className="text-2xl">›</span>
              </button>
            )}
          </>
        ) : (
          <p className="py-6 text-center italic text-[#ffe0a1]" style={{ fontFamily: 'Georgia, serif' }}>No results found for “{query}”</p>
        )}
      </div>
    </div>
  );
};

const RadarButton = ({ onClick }) => (
  <button
    onClick={onClick}
    className="p-1.5 rounded-lg bg-green-500/15 border border-green-500/40 text-green-300 hover:bg-green-500/25 transition-colors"
    title="Dragon Radar"
    aria-label="Open Dragon Radar"
    data-testid="navbar-radar-btn"
  >
    <Radar className="w-4 h-4" />
  </button>
);
const openRadar = () => emitEvent(RADAR_OPEN);

const WORDMARK_IMAGE = "https://customer-assets-rejwkqb3.emergentagent.net/job_dragon-sphere-tv/artifacts/434e6664f00c6ecf_1793246551377.webp";
const LOGO_IMAGE = "https://customer-assets.emergentagent.com/job_dragon-sphere-tv/artifacts/c5tmpjar_Untitled129.png";

export const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [friendRequestCount, setFriendRequestCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const mobileSearchAreaRef = useRef(null);
  useEffect(() => {
    const dismissMobileSearch = (event) => {
      if (mobileSearchAreaRef.current && !mobileSearchAreaRef.current.contains(event.target)) {
        setShowSearch(false);
      }
    };
    document.addEventListener('pointerdown', dismissMobileSearch);
    return () => document.removeEventListener('pointerdown', dismissMobileSearch);
  }, []);
  const [dragonBalls, setDragonBalls] = useState([]);
  const [canCollect, setCanCollect] = useState(true);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const [showDragonBallModal, setShowDragonBallModal] = useState(false);
  const [showShenronModal, setShowShenronModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [zeniBalance, setZeniBalance] = useState(0);
  const cooldownEndRef = useRef(null);
  const searchRequestRef = useRef(0);

  // Load friend request count and dragon balls together
  useEffect(() => {
    if (user) {
      loadUserData();
      const interval = setInterval(loadUserData, 30000); // Check every 30s
      return () => clearInterval(interval);
    }
  }, [user]);

  // Server-synced cooldown timer for navbar
  useEffect(() => {
    if (!cooldownEndRef.current || canCollect) return;
    
    const updateCooldown = () => {
      const now = Date.now();
      const remaining = Math.max(0, Math.floor((cooldownEndRef.current - now) / 1000));
      
      if (remaining <= 0) {
        setCanCollect(true);
        setCooldownRemaining(0);
        cooldownEndRef.current = null;
        loadUserData(); // Reload from server
      } else {
        setCooldownRemaining(remaining);
      }
    };
    
    updateCooldown();
    const interval = setInterval(updateCooldown, 1000);
    return () => clearInterval(interval);
  }, [canCollect]);

  // Consolidated data loading for navbar
  const loadUserData = async () => {
    try {
      const [friendsRes, dragonBallRes, shopRes] = await Promise.all([
        friendAPI.getFriends().catch(() => ({ data: { requests_received: [] } })),
        dragonBallAPI.getCollection().catch(() => ({ data: { collected: [], can_collect: true, cooldown_remaining: 0 } })),
        shopAPI.getBalance().catch(() => ({ data: { zeni: 0 } }))
      ]);
      
      setFriendRequestCount(friendsRes.data.requests_received?.length || 0);
      setDragonBalls(dragonBallRes.data.collected || []);
      setCanCollect(dragonBallRes.data.can_collect);
      setZeniBalance(shopRes.data.zeni || 0);
      
      // Calculate cooldown end time from server response
      if (!dragonBallRes.data.can_collect && dragonBallRes.data.cooldown_remaining > 0) {
        cooldownEndRef.current = Date.now() + (dragonBallRes.data.cooldown_remaining * 1000);
        setCooldownRemaining(dragonBallRes.data.cooldown_remaining);
      } else {
        cooldownEndRef.current = null;
        setCooldownRemaining(0);
      }
    } catch (error) {
      console.error('Failed to load navbar data');
    }
  };

  // Subscribe to Dragon Ball events for real-time updates
  useEffect(() => {
    const unsubscribeCollected = subscribeToEvent(DRAGON_BALL_COLLECTED, (data) => {
      setDragonBalls(data.collected || []);
    });
    
    const unsubscribeWish = subscribeToEvent(DRAGON_BALL_WISH_MADE, () => {
      loadDragonBalls();
    });
    // Summon button inside the Dragon Radar opens the existing Shenron wish modal
    const unsubscribeSummon = subscribeToEvent(RADAR_SUMMON, () => setShowShenronModal(true));
    
    return () => {
      unsubscribeCollected();
      unsubscribeWish();
      unsubscribeSummon();
    };
  }, []);

  // Subscribe to Friend request events for real-time updates
  useEffect(() => {
    const events = [FRIEND_REQUEST_SENT, FRIEND_REQUEST_ACCEPTED, FRIEND_REQUEST_REJECTED, FRIEND_REMOVED];
    const unsubscribers = events.map(event => 
      subscribeToEvent(event, () => loadFriendRequests())
    );
    
    return () => unsubscribers.forEach(unsub => unsub());
  }, []);

  const loadFriendRequests = async () => {
    try {
      const response = await friendAPI.getFriends();
      setFriendRequestCount(response.data.requests_received?.length || 0);
    } catch (error) {
      console.error('Failed to load friend requests');
    }
  };

  const loadDragonBalls = async () => {
    try {
      const response = await dragonBallAPI.getCollection();
      setDragonBalls(response.data.collected || []);
      setCanCollect(response.data.can_collect);
      setCooldownRemaining(response.data.cooldown_remaining || 0);
    } catch (error) {
      console.error('Failed to load Dragon Ball collection');
    }
  };

  const handleWishComplete = () => {
    // Reload dragon ball status after wish
    loadDragonBalls();
  };

  // Debounced search
  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  // Keyboard shortcut for search (Ctrl+K or Cmd+K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Check for Ctrl+K or Cmd+K
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setShowSearch(true);
        // Focus the search input after a short delay
        setTimeout(() => {
          const searchInput = document.querySelector('[data-testid="global-search-input"]');
          if (searchInput) searchInput.focus();
        }, 100);
      }
      // Close search on Escape
      if (e.key === 'Escape' && showSearch) {
        setShowSearch(false);
        setSearchQuery('');
        setSearchResults([]);
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showSearch]);

  // Debounce requests and discard stale responses when typing quickly.
  useEffect(() => {
    const query = searchQuery.trim();
    const requestId = ++searchRequestRef.current;
    if (query.length < 2) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }
    setIsSearching(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await searchAPI.search(query, 15, true);
        if (requestId !== searchRequestRef.current) return;
        const local = [
          ...SEARCH_PAGES.filter(([label]) => label.toLowerCase().includes(query.toLowerCase()))
            .map(([name, path]) => ({ id: path, name, path, type: 'page' })),
          ...FLASH_GAMES.filter(game => `${game.title} ${game.series}`.toLowerCase().includes(query.toLowerCase()))
            .map(game => ({ id: game.id, name: game.title, type: 'game', path: `/games?game=${encodeURIComponent(game.id)}` })),
        ];
        const combined = [
          ...(data.users || []).filter(item => item.id !== user?.id).map(item => ({ ...item, name: item.username, type: 'user' })),
          ...local,
          ...(data.series || []).map(item => ({ ...item, type: 'series' })),
          ...(data.seasons || []).map(item => ({ ...item, type: 'season' })),
          ...(data.episodes || []).map(item => ({ ...item, type: 'episode', name: item.title })),
        ];
        setSearchResults(combined.sort((a, b) => searchRank(a.name || '', query.toLowerCase()) - searchRank(b.name || '', query.toLowerCase())).slice(0, 45));
        setSearchError(false);
      } catch (error) {
        if (requestId !== searchRequestRef.current) return;
        console.error('Search failed:', error);
        setSearchResults([]);
        setSearchError(true);
      } finally {
        if (requestId === searchRequestRef.current) setIsSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, user?.id]);

  const handleSearchResultClick = (result) => {
    setShowSearch(false);
    setSearchQuery('');
    setSearchResults([]);
    setMobileMenuOpen(false);
    
    if (result.type === 'page' || result.type === 'game') {
      navigate(result.path);
    } else if (result.type === 'user') {
      navigate(`/profile/${result.id}`);
    } else if (result.type === 'series') {
      navigate(`/series/${result.id}`);
    } else if (result.type === 'season') {
      navigate(`/series/standalone/${result.id}`);
    } else if (result.type === 'episode') {
      navigate(`/watch/${result.id}`);
    }
  };

  const getResultIcon = (type) => {
    switch (type) {
      case 'page': return Hash;
      case 'game': return Gamepad2;
      case 'user': return User;
      case 'series': return Layers;
      case 'season': return Tv;
      case 'episode': return Film;
      default: return Search;
    }
  };

  const getResultColor = (type) => {
    switch (type) {
      case 'user': return 'bg-green-500/20 text-green-500';
      case 'series': return 'bg-primary/20 text-primary';
      case 'season': return 'bg-yellow-500/20 text-yellow-500';
      case 'episode': return 'bg-blue-500/20 text-blue-500';
      default: return 'bg-gray-500/20 text-gray-500';
    }
  };

  // Swipe handlers for mobile menu
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => setMobileMenuOpen(false),
    onSwipedRight: () => setMobileMenuOpen(true),
    preventScrollOnSwipe: true,
    trackTouch: true,
    trackMouse: false,
    delta: 50,
    swipeDuration: 500,
  });

  // Single source of truth for nav items - keeps desktop and mobile in sync
  const navItems = [
    { to: '/profile', label: 'Profile', icon: User, showWhen: 'always', highlight: true },
    { to: '/friends', label: 'Friends', icon: UserPlus, showWhen: 'always', badge: friendRequestCount },
    { to: '/channels', label: 'Channels', icon: Hash, showWhen: 'always' },
    { to: '/groups', label: 'Groups', icon: MessageSquare, showWhen: 'always' },
    { to: '/library', label: 'Library', icon: Video, showWhen: 'always' },
    { to: '/games', label: 'Games', icon: Gamepad2, showWhen: 'always' },
    { to: '/news', label: 'News', icon: Newspaper, showWhen: 'always' },
    { to: '/leaderboard', label: 'Leaderboard', icon: Trophy, showWhen: 'always' },
    { to: '/admin', label: 'Upload', icon: Shield, showWhen: 'uploaderOrAbove' },
    { to: '/users', label: 'Users', icon: Users, showWhen: 'adminOrOwner' },
    { to: '/owner/reports', label: 'Reports', icon: AlertTriangle, showWhen: 'ownerOnly' },
    { to: '/channel-analytics', label: 'Analytics', icon: BarChart3, showWhen: 'adminOrOwner' },
  ];

  const shouldShowItem = (item) => {
    if (item.showWhen === 'always') return true;
    if (item.showWhen === 'uploaderOrAbove') return ['uploader', 'admin', 'owner'].includes(user?.role);
    if (item.showWhen === 'adminOrOwner') return user?.role === 'admin' || user?.role === 'owner';
    if (item.showWhen === 'ownerOnly') return user?.role === 'owner';
    return false;
  };

  const visibleNavItems = navItems.filter(shouldShowItem);

  return (
    <nav className="dragon-nav-accent fixed top-0 left-0 right-0 z-50 border-b border-white/10 backdrop-blur-md bg-background/80" {...swipeHandlers}>
      <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-6">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center flex-shrink-0">
          <Link to="/" className="flex items-center gap-1.5 group flex-shrink-0" data-testid="nav-logo">
            <img 
              src={LOGO_IMAGE} 
              alt="Dragon TV" 
              className="w-8 h-8 2xl:w-10 2xl:h-10 object-contain group-hover:scale-110 transition-transform drop-shadow-[0_0_10px_rgba(255,140,0,0.6)]"
            />
            <img
              src={WORDMARK_IMAGE}
              alt="Dragon TV"
              className="h-7 2xl:h-9 w-auto object-contain group-hover:scale-105 transition-transform"
              data-testid="navbar-wordmark"
            />
          </Link>
          {user && <HalloweenShopButton />}
          </div>
          
          {/* Discord Link */}
          <a 
            href="https://discord.gg/dragontv" 
            target="_blank" 
            rel="noopener noreferrer"
            className="ml-2 p-1.5 hover:bg-white/10 rounded-lg transition-colors group"
            title="Join our Discord"
            data-testid="nav-discord"
          >
            <svg 
              className="w-5 h-5 text-[#5865F2] group-hover:scale-110 transition-transform" 
              viewBox="0 0 24 24" 
              fill="currentColor"
            >
              <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
            </svg>
          </a>

          {/* Desktop Navigation - show on lg screens (1024px+) */}
          <div className="hidden lg:flex items-center gap-1">
            {user ? (
              <>
                {visibleNavItems.filter(item => !item.highlight).map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className="flex items-center justify-center p-2 hover:text-primary hover:bg-white/5 rounded-lg transition-colors relative"
                    data-testid={`nav-${item.label.toLowerCase()}`}
                    title={item.label}
                  >
                    <item.icon className="w-4 h-4 flex-shrink-0" />
                    {item.badge > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[9px] w-4 h-4 flex items-center justify-center rounded-full">
                        {item.badge}
                      </span>
                    )}
                  </Link>
                ))}
                
                {/* Shenron search: artwork is decorative, native input stays accessible. */}
                <div className="relative ml-1 shenron-search-container" style={{ width: "clamp(170px, 15vw, 245px)", flexShrink: 1 }}>
                  <div className="shenron-search-art relative z-[60]">
                    <input
                      type="search"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onFocus={() => setShowSearch(true)}
                      onKeyDown={(e) => { if (e.key === 'Enter' && searchResults.length) handleSearchResultClick(searchResults[0]); }}
                      placeholder="Search Dragon TV..."
                      className="shenron-search-input"
                      aria-label="Search Dragon TV"
                      data-testid="global-search-input"
                    />
                    <button type="button" className="shenron-search-submit" aria-label="Open search results" onClick={() => { setShowSearch(true); document.querySelector('[data-testid="global-search-input"]')?.focus(); }} />
                  </div>
                  {/* Shenron universal search results: desktop */}
                  <AnimatePresence>
                    {showSearch && searchQuery.trim().length >= 2 && (
                      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="absolute top-[68%] left-0 right-0 z-50 w-full">
                        <div className="w-full"><ShenronSearchResults compact results={searchResults} query={searchQuery} loading={isSearching} error={searchError} onSelect={handleSearchResultClick} onDM={result => { setShowSearch(false); setSearchQuery(''); navigate(`/dm/${result.id}`); }} /></div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {showSearch && searchQuery.length >= 2 && (
                    <div 
                      className="fixed inset-0 z-30" 
                      onClick={() => setShowSearch(false)}
                    />
                  )}
                </div>
                
                <div className="flex items-center gap-1 flex-shrink-0">
                  {/* Zeni Balance with Dropdown */}
                  <div className="relative group">
                    <Link
                      to="/shop"
                      className="flex items-center gap-1 px-2 py-1 bg-yellow-500/10 hover:bg-yellow-500/20 rounded-lg transition-colors cursor-pointer"
                      title="Zeni Shop"
                      data-testid="nav-zeni"
                    >
                      <span className="text-yellow-400 text-xs">Z</span>
                      <span className="text-yellow-300 text-xs font-bold">{zeniBalance.toLocaleString()}</span>
                      <ChevronDown className="w-3 h-3 text-yellow-400/70" />
                    </Link>
                    {/* Dropdown Menu */}
                    <div className="absolute right-0 top-full mt-1 w-36 bg-card border border-white/10 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                      <Link
                        to="/shop"
                        className="flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:bg-white/5 hover:text-white rounded-t-lg transition-colors"
                        data-testid="nav-shop-link"
                      >
                        <ShoppingBag className="w-4 h-4" />
                        Shop
                      </Link>
                      <Link
                        to="/inventory"
                        className="flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:bg-white/5 hover:text-white rounded-b-lg transition-colors"
                        data-testid="nav-inventory-link"
                      >
                        <Package className="w-4 h-4" />
                        Inventory
                      </Link>
                    </div>
                  </div>
                  
                  {/* Ad-Free Indicator */}
                  <AdFreeIndicator />
                  
                  {/* Dragon Radar (collection, summon, detection) */}
                  <RadarButton onClick={openRadar} />
                  
                  <Link 
                    to="/profile"
                    className="flex items-center gap-1 hover:bg-white/5 px-1.5 py-1 rounded-lg transition-colors"
                    data-testid="nav-profile"
                  >
                    <div className="flex flex-col items-end">
                      <span className="text-[9px] uppercase tracking-wide text-accent font-bold">
                        {user.role}
                      </span>
                      <span className="text-[10px] text-gray-300 max-w-[60px] truncate">{user.username}</span>
                    </div>
                  </Link>
                  <button
                    onClick={logout}
                    className="p-1.5 hover:bg-white/5 rounded-full transition-colors"
                    data-testid="nav-logout-btn"
                  >
                    <LogOut className="w-4 h-4" />
                  </button>
                </div>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-sm uppercase font-bold tracking-widest hover:text-primary transition-colors"
                  data-testid="nav-login"
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="bg-primary hover:bg-orange-600 text-white font-black uppercase tracking-widest px-6 py-3 rounded-full hover:scale-105 transition-transform duration-200 shadow-[0_0_20px_rgba(248,91,26,0.5)]"
                  data-testid="nav-register-btn"
                >
                  Register
                </Link>
              </>
            )}
          </div>

          {/* Mobile Dragon Ball indicator (visible on screens below lg) */}
          {user && (
            <div className="lg:hidden flex items-center gap-2">
              <AdFreeIndicator />
              <RadarButton onClick={openRadar} />
            </div>
          )}

          {/* Mobile menu button (visible on screens below lg) */}
          <button
            onClick={() => { setShowSearch(false); setMobileMenuOpen(!mobileMenuOpen); }}
            className="lg:hidden p-2 hover:bg-white/5 rounded-lg transition-colors"
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6 text-white" />
            ) : (
              <Menu className="w-6 h-6 text-white" />
            )}
          </button>
        </div>

        {/* Mobile Navigation (visible on screens below lg) */}
        {mobileMenuOpen && (
          <div 
            className="lg:hidden border-t border-white/10 py-4 space-y-3 bg-background/95 backdrop-blur-lg max-h-[calc(100dvh-4rem)] overflow-y-auto"
            style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 32px) + 7rem)' }}
          >
            {user ? (
              <>
                <div className="px-4 py-2 bg-white/5 rounded-lg mb-3 flex items-center justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-accent font-bold">
                      {user.role}
                    </div>
                    <div className="text-sm text-white font-medium">{user.username}</div>
                  </div>
                  {/* Dragon Radar - Mobile menu */}
                  <RadarButton onClick={() => { openRadar(); setMobileMenuOpen(false); }} />
                </div>
                
                {/* Shenron search inside the mobile menu. Desktop navigation remains unchanged. */}
                <div className="w-full max-w-[620px] mx-auto px-4 mb-3">
                  <div className="relative z-20 w-full mx-auto" style={{ aspectRatio: '3 / 1' }}>
                    <img src="/shenron-search.png" alt="" aria-hidden="true" draggable="false" className="absolute inset-0 w-full h-full object-contain pointer-events-none select-none" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter' && searchResults.length) handleSearchResultClick(searchResults[0]); }}
                      placeholder="Search Dragon TV..."
                      aria-label="Search Dragon TV"
                      autoComplete="off"
                      className="absolute z-30 bg-transparent border-0 outline-none focus:outline-none focus:ring-0 ring-0 text-[#fff1d9] placeholder:text-[#fff1d9]/85 italic font-light tracking-[0.025em] min-w-0"
                      style={{ left: '25%', top: '39%', width: '49%', height: '22%', padding: '0 1%', fontSize: 'clamp(10px, 2.5vw, 21px)', fontFamily: 'Georgia, Times New Roman, serif', textShadow: '0 0 5px rgba(255,226,180,.8), 0 0 13px rgba(255,170,65,.45)' }}
                      onFocus={() => setShowSearch(true)}
                      data-testid="mobile-search-input"
                    />
                    {searchQuery && (
                      <button type="button" aria-label="Clear search" onClick={() => { setSearchQuery(''); setSearchResults([]); }} className="absolute z-10 text-white/80 hover:text-white text-lg" style={{ right: '23%', top: '41%', height: '19%', width: '4%' }}>×</button>
                    )}
                    <button type="button" aria-label="Search" onClick={() => { setShowSearch(true); document.querySelector('[data-testid="mobile-search-input"]')?.focus(); }} className="absolute z-10 bg-transparent border-0 cursor-pointer" style={{ left: '76%', top: '32%', width: '12%', height: '37%' }} />
                  </div>

                  {/* Shenron universal search results: mobile menu */}
                  {showSearch && searchQuery.trim().length >= 2 && (
                    <div className="relative z-10 -mt-[12%] px-[2%]">
                      <ShenronSearchResults mobile results={searchResults} query={searchQuery} loading={isSearching} error={searchError} onSelect={handleSearchResultClick} onDM={result => { setShowSearch(false); setMobileMenuOpen(false); setSearchQuery(''); navigate(`/dm/${result.id}`); }} />
                    </div>
                  )}
                </div>

                                {/* Zeni & Shop - Mobile */}
                <div className="px-4 mb-3">
                  <div className="flex items-center gap-2 p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
                    <div className="flex items-center gap-2">
                      <Coins className="w-5 h-5 text-yellow-400" />
                      <span className="text-yellow-300 font-bold">{zeniBalance.toLocaleString()} Zeni</span>
                    </div>
                    <div className="flex-1" />
                    <Link
                      to="/shop"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-1 px-3 py-1.5 bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-300 rounded-lg text-sm font-bold transition-colors"
                      data-testid="mobile-nav-shop"
                    >
                      <ShoppingBag className="w-4 h-4" />
                      Shop
                    </Link>
                    <Link
                      to="/inventory"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-1 px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm font-bold transition-colors"
                      data-testid="mobile-nav-inventory"
                    >
                      <Package className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
                
                {visibleNavItems.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 text-sm uppercase font-bold tracking-widest hover:bg-white/5 rounded-lg transition-colors ${item.highlight ? 'text-accent' : ''}`}
                    data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                  >
                    <item.icon className="w-5 h-5" />
                    {item.label}
                    {item.badge > 0 && (
                      <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full ml-auto">
                        {item.badge}
                      </span>
                    )}
                  </Link>
                ))}
                <button
                  onClick={() => {
                    logout();
                    setMobileMenuOpen(false);
                  }}
                  className="flex items-center gap-3 px-4 py-3 text-sm uppercase font-bold tracking-widest hover:bg-white/5 rounded-lg transition-colors w-full text-left text-red-400"
                  data-testid="mobile-nav-logout"
                >
                  <LogOut className="w-5 h-5" />
                  Logout
                </button>
                <button
                  onClick={() => {
                    setShowReportModal(true);
                    setMobileMenuOpen(false);
                  }}
                  className="flex items-center gap-3 px-4 py-3 text-sm uppercase font-bold tracking-widest hover:bg-white/5 rounded-lg transition-colors w-full text-left text-yellow-400 mb-4"
                  data-testid="mobile-nav-report"
                >
                  <Flag className="w-5 h-5" />
                  Report
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-4 py-3 text-sm uppercase font-bold tracking-widest hover:bg-white/5 rounded-lg transition-colors"
                  data-testid="mobile-nav-login"
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block bg-primary hover:bg-orange-600 text-white font-black uppercase tracking-widest px-4 py-3 rounded-lg text-center"
                  data-testid="mobile-nav-register"
                >
                  Register
                </Link>
              </>
            )}
          </div>
        )}
      </div>
      
      {/* Dragon Ball Collection Modal */}
      <AnimatePresence>
        {showDragonBallModal && (
          <DragonBallCollection 
            collected={dragonBalls} 
            onClose={() => setShowDragonBallModal(false)}
            onSummon={() => setShowShenronModal(true)}
            cooldownRemaining={cooldownRemaining}
            canCollect={canCollect}
          />
        )}
      </AnimatePresence>
      
      {/* Shenron Wish Modal */}
      <AnimatePresence>
        {showShenronModal && (
          <ShenronWishModal
            isOpen={showShenronModal}
            onClose={() => setShowShenronModal(false)}
            onWishComplete={handleWishComplete}
          />
        )}
      </AnimatePresence>
      
      {/* Report Modal */}
      <ReportModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
      />
    </nav>
  );
};
